import React from 'react';
import {
  Alert,
  Box,
  Divider,
  Grid,
  LinearProgress,
  Link,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useSelector } from 'react-redux';
import SubscriptionStatusChip from './SubscriptionStatusChip';
import './SubscriptionSection.css';
import InvoiceStatusChip from './InvoiceStausChip';
import dayjs from 'dayjs';
import { useTranslatedMessages } from '../../../hooks/useTranslatedMessages';
import appConfig from '../../../config/appConfig';
import NoActiveSubscriptionAlert from '../../../components/BottomBar/NoActiveSubscriptionAlert';
import { buildSubscriptionUsageDisplayItems } from '../../../utils/subscriptionUsage';

const SubscriptionSection = () => {
  const subscription = useSelector((state) => state.subscription.subscription);
  const user = useSelector((state) => state.user.user);
  const { settings: msg, direction } = useTranslatedMessages();

  if (!user?.is_root_user) {
    return null;
  }

  if (!subscription) {
    return (
      <Alert
        severity="warning"
        sx={{
          borderRadius: 0,
          display: 'flex',
          alignItems: 'center',
          minHeight: '60px',
          justifyContent: 'center',
          py: 1,
          px: 3,
        }}
      >
        <NoActiveSubscriptionAlert
          isFromPartner={subscription?.is_from_partner || false}
          partnerEmail={subscription?.partner_org_email || ''}
        />
      </Alert>
    );
  }
  const periodSeparator = direction === 'rtl' ? 'إلى' : 'to';
  const dueInvoices = subscription?.unpaid_invoices ?? [];
  const usageItems = buildSubscriptionUsageDisplayItems(subscription);
  const planTitle = `${subscription?.type ?? '-'} (${subscription?.recurrence_limit ?? '-'})`;
  return (
    <Paper elevation={0} sx={{ p: '1rem' }}>
      <Box sx={{ mb: '1.5rem' }}>
        <Typography variant="h6">{msg.subscription.details_title}</Typography>
        <Divider></Divider>
      </Box>
      <Grid container spacing={2}>
        <Grid size={2}>
          <Typography variant="subtitle1" className={'label'}>
            {msg.subscription.status_title}
          </Typography>
        </Grid>
        <Grid size={10}>
          <SubscriptionStatusChip status={subscription.status} />
        </Grid>
        <Grid size={2}>
          <Typography variant="subtitle1" className={'label'}>
            {msg.subscription.current_period}
          </Typography>
        </Grid>
        <Grid size={10}>
          <Typography variant="subtitle1">
            <Box
              component="span"
              sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, direction: 'ltr', unicodeBidi: 'isolate' }}
            >
              <Box component="span">{dayjs.unix(subscription.current_period_start).format('DD-MM-YYYY HH:mm:ss')}</Box>
              <Box component="span" sx={{ direction: 'rtl', unicodeBidi: 'isolate' }}>
                {periodSeparator}
              </Box>
              <Box component="span">{dayjs.unix(subscription.current_period_end).format('DD-MM-YYYY HH:mm:ss')}</Box>
            </Box>
          </Typography>
        </Grid>
        <Grid size={2}>
          <Typography variant="subtitle1" className={'label'}>
            {msg.subscription.started}
          </Typography>
        </Grid>
        <Grid size={10}>
          <Typography variant="subtitle1">
            {dayjs.unix(subscription.created_at).format('DD-MM-YYYY HH:mm:ss')}
          </Typography>
        </Grid>
        <Grid size={2}>
          <Typography variant="subtitle1" className={'label'}>
            {msg.subscription.next_invoice}
          </Typography>
        </Grid>
        <Grid size={10}>
          <Typography variant="subtitle1">
            {dayjs.unix(subscription.current_period_end).format('DD-MM-YYYY HH:mm:ss')}
          </Typography>
        </Grid>
      </Grid>
      <Box sx={{ mt: '1.5rem', mb: '1.5rem' }}>
        <Typography variant="h6">{msg.subscription.due_invoices_title}</Typography>
        <Divider></Divider>
      </Box>
      <TableContainer component={Box}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{msg.subscription.amount}</TableCell>
              <TableCell>{msg.subscription.status}</TableCell>
              <TableCell>{msg.subscription.due_date}</TableCell>
              <TableCell>{msg.subscription.created}</TableCell>
              <TableCell>{msg.subscription.payment_link}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {dueInvoices.length ? (
              dueInvoices.map((row) => (
                <TableRow key={row.stripe_invoice_id}>
                  <TableCell component="th" scope="row">
                    {(Number(row.amount || 0) / 100).toLocaleString('en-US', {
                      style: 'currency',
                      currency: (row.currency || appConfig.stripeCurrency).toUpperCase(),
                    })}
                  </TableCell>
                  <TableCell>
                    <InvoiceStatusChip
                      invoiceStatus={new Date() > new Date(row.due_date * 1000) ? 'Past Due' : 'Open'}
                    />
                  </TableCell>
                  <TableCell>{dayjs.unix(row.due_date).format('ll')}</TableCell>
                  <TableCell>{dayjs.unix(row.created_at).format('ll')}</TableCell>
                  <TableCell>
                    {row.hosted_invoice_url ? (
                      <Link href={row.hosted_invoice_url} target="_blank" rel="noopener noreferrer" underline="hover">
                        {msg.subscription.pay_now}
                      </Link>
                    ) : (
                      '-'
                    )}
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={5} align={'center'}>
                  {msg.subscription.no_due_invoices}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <Box sx={{ mt: '1.5rem', mb: '1.5rem' }}>
        <Typography variant="h6">{msg.subscription.usage_tracking_title}</Typography>
        <Divider></Divider>
      </Box>
      <Box sx={{ mt: 2 }}>
        {usageItems.length ? (
          usageItems.map((usageItem) => (
            <Box key={usageItem.meterCode} sx={{ mb: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={2}>
                  <Typography variant="subtitle1" className={'label'}>
                    {usageItem.meterName}
                  </Typography>
                </Grid>
                <Grid item xs={10}>
                  <Typography variant="subtitle1">
                    {usageItem.used} / {usageItem.isUnlimited ? '∞' : usageItem.limit}
                    <Typography component="span" color="textSecondary" sx={{ ml: 1 }}></Typography>
                    {usageItem.isUnlimited && (
                      <Typography component="span" color="primary" sx={{ ml: 1 }}>
                        ({msg.subscription.unlimited})
                      </Typography>
                    )}
                  </Typography>
                </Grid>
              </Grid>
              {!usageItem.isUnlimited && (
                <Box sx={{ mt: 1, width: '40%' }}>
                  <LinearProgress
                    variant="determinate"
                    value={usageItem.progress}
                    sx={{
                      height: '12px',
                      borderRadius: '6px',
                      backgroundColor: '#e0e0e0',
                      '& .MuiLinearProgress-bar': {
                        backgroundColor: usageItem.progress >= 90 ? '#f44336' : '#2196f3',
                        borderRadius: '6px',
                      },
                    }}
                  />
                  <Typography
                    variant="caption"
                    color={usageItem.progress >= 90 ? 'error' : 'textSecondary'}
                    sx={{ mt: 0.5, display: 'block' }}
                  >
                    {usageItem.progress.toFixed(1)}% used
                  </Typography>
                </Box>
              )}
            </Box>
          ))
        ) : (
          <Typography variant="subtitle1">-</Typography>
        )}
      </Box>
    </Paper>
  );
};

export default SubscriptionSection;
